import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';

const BATCH_SIZE = 10;
const BATCH_DELAY_MS = 250;

const naira = (a: any) => '₦' + Number(a || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const statusPill = (s: string) => {
  switch (s) {
    case 'paid': return 'bg-green-100 text-green-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'failed': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

type ParsedRow = {
  tracking_number: string;
  receiver_name: string;
  email: string;
  amount: number;
  valid: boolean;
  reason: string | null;
};

type ResultRow = { tracking_number: string; status: 'created' | 'skipped' | 'failed'; message: string };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function CustomDutyPayments() {
  const [tab, setTab] = useState<'issue' | 'records'>('issue');

  // ── Upload / issue state ───────────────────────────────────
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [summary, setSummary] = useState<{ total: number; valid: number; invalid: number } | null>(null);

  const [progress, setProgress] = useState({
    running: false,
    done: false,
    total: 0,
    processed: 0,
    created: 0,
    skipped: 0,
    failed: 0,
    currentBatch: 0,
    totalBatches: 0,
  });
  const [results, setResults] = useState<ResultRow[]>([]);

  const resetIssue = () => {
    setFile(null);
    setBatchId(null);
    setRows([]);
    setSummary(null);
    setResults([]);
    setProgress({ running: false, done: false, total: 0, processed: 0, created: 0, skipped: 0, failed: 0, currentBatch: 0, totalBatches: 0 });
  };

  const parseFile = () => {
    if (!file) { toast.error('Choose a file first.'); return; }
    setParsing(true);
    setRows([]);
    setSummary(null);
    setResults([]);
    const fd = new FormData();
    fd.append('file', file);
    SuperAdminApis.customDutyParse(fd)
      .then((res: any) => {
        const data = res?.data?.data;
        if (!data || !data.rows?.length) {
          toast.error('No rows found. Check the file has the required columns.');
          return;
        }
        setBatchId(data.batch_id);
        setRows(data.rows);
        setSummary(data.summary);
      })
      .catch((e: any) => toast.error(e?.response?.data?.message || 'Could not parse the file.'))
      .finally(() => setParsing(false));
  };

  const sendInvoices = async () => {
    const valid = rows.filter((r) => r.valid);
    if (!batchId || valid.length === 0) { toast.error('Nothing valid to send.'); return; }

    const batches: ParsedRow[][] = [];
    for (let i = 0; i < valid.length; i += BATCH_SIZE) batches.push(valid.slice(i, i + BATCH_SIZE));

    setResults([]);
    setProgress({
      running: true, done: false, total: valid.length, processed: 0,
      created: 0, skipped: 0, failed: 0, currentBatch: 0, totalBatches: batches.length,
    });

    let created = 0, skipped = 0, failed = 0, processed = 0;
    const acc: ResultRow[] = [];

    for (let b = 0; b < batches.length; b++) {
      const batch = batches[b];
      setProgress((p) => ({ ...p, currentBatch: b + 1 }));

      try {
        const res: any = await SuperAdminApis.customDutyProcessBatch({
          batch_id: batchId,
          rows: batch.map((r) => ({
            tracking_number: r.tracking_number,
            receiver_name: r.receiver_name,
            email: r.email,
            amount: r.amount,
          })),
        });
        const data = res?.data?.data;
        created += data?.created || 0;
        skipped += data?.skipped || 0;
        failed += data?.failed || 0;
        (data?.rows || []).forEach((row: ResultRow) => acc.push(row));
      } catch (e: any) {
        // Whole batch failed to reach/return — count each row as failed, keep going.
        failed += batch.length;
        batch.forEach((r) => acc.push({ tracking_number: r.tracking_number, status: 'failed', message: e?.response?.data?.message || 'Request failed' }));
      }

      processed += batch.length;
      setResults([...acc]);
      setProgress((p) => ({ ...p, processed, created, skipped, failed }));

      if (b < batches.length - 1) await sleep(BATCH_DELAY_MS);
    }

    setProgress((p) => ({ ...p, running: false, done: true }));
    toast.success(`Done — ${created} invoice(s) sent${skipped ? `, ${skipped} skipped` : ''}${failed ? `, ${failed} failed` : ''}.`);
  };

  // ── Records state ──────────────────────────────────────────
  const [records, setRecords] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({ current_page: 1, last_page: 1, total: 0 });
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid' | 'failed'>('all');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const listParams = useCallback((page: number) => ({
    page,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: search || undefined,
    from: from || undefined,
    to: to || undefined,
  }), [statusFilter, search, from, to]);

  const loadRecords = useCallback((page = 1) => {
    setListLoading(true);
    SuperAdminApis.getCustomDutyList(listParams(page))
      .then((res: any) => {
        const p = res?.data?.data;
        setRecords(p?.data || []);
        setPagination({ current_page: p?.current_page || 1, last_page: p?.last_page || 1, total: p?.total || 0 });
      })
      .catch(() => toast.error('Could not load records.'))
      .finally(() => setListLoading(false));
  }, [listParams]);

  useEffect(() => {
    if (tab === 'records') loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter]);

  const exportRecords = () => {
    setExporting(true);
    SuperAdminApis.exportCustomDuty({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: search || undefined, from: from || undefined, to: to || undefined,
    })
      .then((res: any) => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `custom-duty-${statusFilter}-${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Export failed.'))
      .finally(() => setExporting(false));
  };

  const validCount = rows.filter((r) => r.valid).length;

  return (
    <>
      <AdminSidebar title="Custom Duty" />
      <div className="relative md:ml-64 bg-[#FAFAFA]">
        <AdminNavbar title="Custom Duty Payments" />
        <div className="lg:px-10 pb-10 px-6 bg-[#FAFAFA] min-h-screen">

          <div className="flex justify-between items-center mt-5 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Custom Duty Payments</h1>
              <p className="text-xs text-gray-500">Issue Paystack duty invoices in bulk and track who has paid.</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => setTab('issue')}
              className={`px-4 py-2 text-sm rounded-lg font-semibold ${tab === 'issue' ? 'bg-[#E04343] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              Issue Invoices
            </button>
            <button
              onClick={() => setTab('records')}
              className={`px-4 py-2 text-sm rounded-lg font-semibold ${tab === 'records' ? 'bg-[#E04343] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              Records
            </button>
          </div>

          {/* ══════════ ISSUE TAB ══════════ */}
          {tab === 'issue' && (
            <div>
              {/* Upload card */}
              <div className="bg-white rounded-lg p-5 shadow-sm mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-1">1. Upload spreadsheet</p>
                <p className="text-xs text-gray-500 mb-3">Columns: <span className="font-mono">TRACKING Number, Receiver Name, EMAIL, AMOUNT</span> (amount in Naira). Excel or CSV.</p>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    disabled={parsing || progress.running}
                    onChange={(e) => { setFile(e.target.files?.[0] ?? null); setRows([]); setSummary(null); setResults([]); }}
                    className="text-sm"
                  />
                  <button
                    onClick={parseFile}
                    disabled={!file || parsing || progress.running}
                    className="px-4 py-2 text-sm rounded-lg bg-gray-800 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                  >
                    {parsing ? 'Reading…' : 'Preview rows'}
                    <Oval visible={parsing} height="15" width="15" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                  </button>
                  {(rows.length > 0 || results.length > 0) && (
                    <button onClick={resetIssue} disabled={progress.running} className="px-3 py-2 text-sm rounded-lg text-gray-500 border border-gray-200 disabled:opacity-50">Clear</button>
                  )}
                </div>
              </div>

              {/* Preview + send */}
              {summary && (
                <div className="bg-white rounded-lg p-5 shadow-sm mb-4">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-700">{summary.total}</span> rows ·
                      <span className="text-green-600 font-semibold"> {summary.valid} valid</span>
                      {summary.invalid > 0 && <span className="text-red-500 font-semibold"> · {summary.invalid} will be skipped</span>}
                    </div>
                    <button
                      onClick={sendInvoices}
                      disabled={progress.running || validCount === 0}
                      className="px-5 py-2 text-sm rounded-lg bg-[#E04343] text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                    >
                      {progress.running ? 'Sending…' : `Send ${validCount} Invoice${validCount === 1 ? '' : 's'}`}
                      <Oval visible={progress.running} height="15" width="15" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                  </div>

                  {/* Progress bar */}
                  {(progress.running || progress.done) && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Batch {progress.currentBatch}/{progress.totalBatches} · {progress.processed}/{progress.total} processed</span>
                        <span>
                          <span className="text-green-600 font-semibold">{progress.created} sent</span>
                          {progress.skipped > 0 && <span className="text-gray-400"> · {progress.skipped} skipped</span>}
                          {progress.failed > 0 && <span className="text-red-500"> · {progress.failed} failed</span>}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-[#E04343] h-2 rounded-full transition-all"
                          style={{ width: `${progress.total ? (progress.processed / progress.total) * 100 : 0}%` }}
                        />
                      </div>
                      {progress.done && <p className="text-xs text-green-600 font-semibold mt-2">✓ Finished. Paystack has emailed each invoice; parcels are now On Hold until paid.</p>}
                    </div>
                  )}

                  {/* Preview table (before send) OR results table (after) */}
                  <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-[11px] text-gray-400 uppercase text-left">
                          <th className="px-3 py-2">Tracking</th>
                          <th className="px-3 py-2">Receiver</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Amount</th>
                          <th className="px-3 py-2">{results.length > 0 ? 'Result' : 'Status'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.length > 0
                          ? results.map((r, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-2 font-medium text-gray-800">{r.tracking_number}</td>
                              <td className="px-3 py-2 text-gray-500" colSpan={2}>{r.message}</td>
                              <td className="px-3 py-2"></td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${r.status === 'created' ? 'bg-green-100 text-green-700' : r.status === 'skipped' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'}`}>
                                  {r.status === 'created' ? 'Sent' : r.status === 'skipped' ? 'Skipped' : 'Failed'}
                                </span>
                              </td>
                            </tr>
                          ))
                          : rows.map((r, i) => (
                            <tr key={i} className={`border-t border-gray-100 ${!r.valid ? 'bg-red-50/40' : ''}`}>
                              <td className="px-3 py-2 font-medium text-gray-800">{r.tracking_number || <span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 text-gray-600">{r.receiver_name || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{r.email || '—'}</td>
                              <td className="px-3 py-2 text-gray-700">{naira(r.amount)}</td>
                              <td className="px-3 py-2">
                                {r.valid
                                  ? <span className="text-green-600 text-xs font-semibold">Ready</span>
                                  : <span className="text-red-500 text-[11px]" title={r.reason || ''}>Skip: {r.reason}</span>}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════ RECORDS TAB ══════════ */}
          {tab === 'records' && (
            <div>
              {/* Status sub-tabs + export */}
              <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                <div className="flex gap-1 bg-white rounded-lg p-1 border border-gray-200">
                  {(['all', 'pending', 'paid', 'failed'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1.5 text-xs rounded-md font-semibold capitalize ${statusFilter === s ? 'bg-[#E04343] text-white' : 'text-gray-500'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  onClick={exportRecords}
                  disabled={exporting}
                  className="px-4 py-2 text-sm rounded-lg bg-gray-800 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                >
                  {exporting ? 'Exporting…' : 'Export'}
                  <Oval visible={exporting} height="15" width="15" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                </button>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-[11px] text-gray-400 uppercase font-semibold">Search</label>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadRecords(1)}
                      placeholder="Tracking, receiver, or email"
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm mt-1"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="text-[11px] text-gray-400 uppercase font-semibold">From</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm mt-1" />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="text-[11px] text-gray-400 uppercase font-semibold">To</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm mt-1" />
                  </div>
                  <button onClick={() => loadRecords(1)} className="px-4 py-2 text-sm rounded-lg bg-[#E04343] text-white font-semibold">Apply</button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="relative overflow-x-auto">
                  {listLoading && <div className="flex justify-center py-8"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>}
                  {!listLoading && (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-[11px] text-gray-400 uppercase text-left">
                          <th className="px-3 py-3">Tracking</th>
                          <th className="px-3 py-3">Receiver</th>
                          <th className="px-3 py-3">Amount</th>
                          <th className="px-3 py-3">Status</th>
                          <th className="px-3 py-3">Invoice</th>
                          <th className="px-3 py-3">Created</th>
                          <th className="px-3 py-3">Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.length === 0 ? (
                          <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-400 text-sm">No records.</td></tr>
                        ) : records.map((r) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="px-3 py-3 font-medium text-gray-800">{r.tracking_number}</td>
                            <td className="px-3 py-3">
                              <div className="text-xs font-semibold text-gray-700">{r.receiver_name || '—'}</div>
                              <div className="text-[10px] text-gray-400">{r.email}</div>
                            </td>
                            <td className="px-3 py-3 text-gray-700">
                              {naira(r.amount)}
                              {r.payment_status === 'paid' && r.amount_paid != null && Number(r.amount_paid) !== Number(r.amount) && (
                                <div className="text-[10px] text-amber-600">paid {naira(r.amount_paid)}</div>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${statusPill(r.payment_status)}`}>{r.payment_status}</span>
                              {r.payment_status === 'failed' && r.failure_reason && (
                                <div className="text-[10px] text-red-400 max-w-[160px] truncate" title={r.failure_reason}>{r.failure_reason}</div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-[11px] text-gray-500">
                              <div>{r.paystack_request_code || '—'}</div>
                              {r.paystack_reference && <div className="text-gray-400">{r.paystack_reference}</div>}
                            </td>
                            <td className="px-3 py-3 text-[11px] text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                            <td className="px-3 py-3 text-[11px] text-gray-500">{r.paid_at ? new Date(r.paid_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {!listLoading && pagination.last_page > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">{pagination.total} total</span>
                    <div className="flex gap-2">
                      <button
                        disabled={pagination.current_page <= 1}
                        onClick={() => loadRecords(pagination.current_page - 1)}
                        className="px-3 py-1 text-xs rounded-md border border-gray-200 text-gray-600 disabled:opacity-40"
                      >Prev</button>
                      <span className="text-xs text-gray-500 px-2 py-1">{pagination.current_page} / {pagination.last_page}</span>
                      <button
                        disabled={pagination.current_page >= pagination.last_page}
                        onClick={() => loadRecords(pagination.current_page + 1)}
                        className="px-3 py-1 text-xs rounded-md border border-gray-200 text-gray-600 disabled:opacity-40"
                      >Next</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </>
  );
}
